import * as React from 'react';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { createSignedConsent, getPublicConsentForm } from '@api/public_endpoints';
import { Button, Text } from '@components/common';
import { PDFView } from '@components/shared/PDFView';
import { useWindowSize } from '@components/utils';

import { ConsentDetailsModal } from './ConsentDetailsModal';
import { ConsentFormParams } from './types';

export interface Props {
  fields: ConsentFormField[];
  url: string;
  token: string;
  language: string;
}

// to make some horizontal paddings
const PDF_WIDTH_MULTIPLIER = 0.9;
const MAX_PAGE_WIDTH = 1024;

export const ConsentFormPage: React.FC<React.PropsWithChildren<Props>> = ({ fields, url, token, language }) => {
  const [loading, setLoading] = useState<boolean>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [formFields, setFormFields] = useState<ConsentFormField[]>([]);
  const [documentURL, setDocumentUrl] = useState<string | null>();
  const [errorMessage, setErrorMessage] = useState<string | null>();

  const { t, i18n } = useTranslation('ConsentFormPage');

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    i18n.changeLanguage(language);
  }, []);

  useEffect(() => {
    const fetchForm = async () => {
      setLoading(true);
      const formsData = await getPublicConsentForm(token);

      if (formsData) {
        setFormFields(formsData.form_fields || []);
        setDocumentUrl(formsData.url);
        setErrorMessage(formsData.error_message);
      }
      setLoading(false);
    };

    fetchForm().catch(console.error);
  }, [token]);

  async function handleSubmit(params: ConsentFormParams) {
    setLoading(true);
    // createParticipationConsent

    const resp = await createSignedConsent(token, params);
    if (resp) {
      window.location.href = `/go/${token}`;
    } else {
      // showToast()
    }
    setLoading(false);
  }
  const pageWidth = (windowWidth || 0) * PDF_WIDTH_MULTIPLIER;
  // limit max page width by window width on mobile and by 5xl (1024px) on desktop
  const width = pageWidth > MAX_PAGE_WIDTH ? MAX_PAGE_WIDTH : pageWidth;

  return (
    <div className='w-full px-6 text-center'>
      <Text h='700' className='mb-6'>
        {t('title')}
      </Text>
      {errorMessage && (
        <Text h='400' className='mt-6'>
          Error: {errorMessage} -- please inform the researcher
        </Text>
      )}
      <div
        onClick={(e) => {
          e.preventDefault();
          if ((e.target as any).tagName.toLowerCase() === 'a') {
            window.open((e.target as any).href);
          }
        }}
      >
        {documentURL && <PDFView width={width} url={documentURL} />}
      </div>
      <div className='fixed inset-x-0 bottom-0 z-50 mt-6 border-t border-gray-200 bg-white p-6'>
        <div className='mx-auto flex max-w-2xl flex-row items-center space-x-4'>
          <Text h='400' bold className='flex-1 text-left'>
            {t('cta_text')}
          </Text>
          <Button disabled={loading} noStyle className='btn-custom-brand' onClick={() => setShowModal(true)}>
            {t('cta_button_text')}
          </Button>
        </div>
      </div>
      {showModal && (
        <ConsentDetailsModal fields={formFields} onCancel={() => setShowModal(false)} onSubmit={handleSubmit} />
      )}
    </div>
  );
};
